/**
 * Pull a VIN out of whatever a barcode or OCR engine produced.
 *
 * Sliding a 17-character window across a page of text is not safe. The check
 * digit rejects roughly ten of every eleven wrong strings, which sounds strong
 * until you try fifty windows. Candidates therefore come from word boundaries.
 */

import { validateVin } from "../vin";

/** VIN characters only — I, O and Q never appear. */
export const VIN_ALPHABET = "ABCDEFGHJKLMNPRSTUVWXYZ0123456789";

/** Position 10 year codes. U, Z and 0 are illegal there. */
const YEAR_CODES = "ABCDEFGHJKLMNPRSTVWXY123456789";
const yearCodePlausible = (vin: string) => YEAR_CODES.includes(vin[9] ?? "");

const fixConfusions = (s: string) =>
  s.replace(/[IO]/g, (c) => (c === "I" ? "1" : "0")).replace(/Q/g, "0");

function candidates(raw: string): string[] {
  const tokens = raw.toUpperCase().split(/[^A-Z0-9]+/).filter(Boolean);
  const out: string[] = [];
  const seen = new Set<string>();
  const add = (s: string) => {
    if (!seen.has(s)) {
      seen.add(s);
      out.push(s);
    }
  };

  for (const t of tokens) {
    if (t.length === 17) add(fixConfusions(t));
  }
  for (let i = 0; i < tokens.length; i++) {
    let joined = "";
    for (let j = i; j < Math.min(i + 3, tokens.length); j++) {
      joined += tokens[j];
      if (joined.length === 17) add(fixConfusions(joined));
      if (joined.length > 17) break;
    }
  }
  for (const t of tokens) {
    if (t.length > 17 && t.length <= 24) {
      for (let i = 0; i + 17 <= t.length; i++) {
        add(fixConfusions(t.slice(i, i + 17)));
      }
    }
  }
  const compact = raw.toUpperCase().replace(/[^A-Z0-9]/g, "");
  if (compact.length >= 17 && compact.length <= 24) {
    for (let i = 0; i + 17 <= compact.length; i++) {
      add(fixConfusions(compact.slice(i, i + 17)));
    }
  }
  return out;
}

/**
 * Characters a camera genuinely confuses on a stamped or printed VIN.
 * All of these are legal VIN characters, so none can simply be substituted —
 * the check digit has to decide.
 */
const CONFUSIONS: Record<string, string> = {
  G: "6", "6": "G", T: "7", "7": "T", B: "8", "8": "B", S: "5", "5": "S",
  Z: "2", "2": "Z", D: "0", "0": "D", A: "4", "4": "A", U: "V", V: "U",
  "1": "L", L: "1", C: "G", E: "F", F: "E",
};

const MAX_REPAIRS = 2;
const MAX_SUGGESTIONS = 3;

export type VinRead =
  | { exact: true; vin: string }
  | { exact: false; raw: string; options: string[] };

function variantsAtDepth(token: string, depth: number): string[] {
  const positions: number[] = [];
  for (let i = 0; i < token.length; i++) {
    if (CONFUSIONS[token[i]!]) positions.push(i);
  }
  const out = new Set<string>();
  const walk = (start: number, current: string, used: number) => {
    if (used === depth) {
      out.add(current);
      return;
    }
    for (let p = start; p < positions.length; p++) {
      const i = positions[p]!;
      const swap = CONFUSIONS[current[i]!];
      if (!swap) continue;
      walk(p + 1, current.slice(0, i) + swap + current.slice(i + 1), used + 1);
    }
  };
  walk(0, token, 0);
  return [...out];
}

export function readVin(raw: string): VinRead | null {
  const cands = candidates(raw);

  for (const c of cands) {
    if (validateVin(c).ok) return { exact: true, vin: c };
  }

  for (let depth = 1; depth <= MAX_REPAIRS; depth++) {
    const hits = new Set<string>();
    let source = "";
    for (const c of cands) {
      if (c.length !== 17) continue;
      for (const v of variantsAtDepth(c, depth)) {
        if (!yearCodePlausible(v)) continue;
        if (validateVin(v).ok) {
          hits.add(v);
          if (!source) source = c;
        }
      }
    }
    if (hits.size === 0) continue;
    if (hits.size > MAX_SUGGESTIONS) return null;
    return { exact: false, raw: source, options: [...hits] };
  }
  return null;
}

export const vinFromBarcode = readVin;
export const vinFromText = readVin;
